
import DateComponent from '@/components/DateComponent.vue';
import { isAfter, isBefore, parse, parseISO } from 'date-fns';
import _ from 'lodash';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Prop, Watch } from 'vue-property-decorator';

@Component({ components: { DateComponent } })
export default class DataTableComponent2 extends Vue {
  @Prop({ default: () => [] }) readonly headers;
  @Prop({ default: () => [] }) readonly items;
  @Prop({ default: 'Nenhum registro encontrado' }) readonly noDataText;
  @Prop({ default: false }) readonly dense;
  @Prop({ default: 'auto' }) readonly height;
  @Prop({ default: true }) readonly fixedHeader;
  @Prop({ default: 'id' }) readonly itemKey;
  @Prop({ default: undefined }) readonly itemsPerPage;
  @Prop({ default: undefined }) readonly footerProps;
  @Prop({ default: false }) readonly loading;
  @Prop({ default: false }) readonly denseHeaders;
  @Prop({ default: false }) readonly hideDefaultFooter;
  @Prop({ default: false }) readonly booleanIcons;
  @Prop({ default: false }) readonly showSelect;
  @Prop({ default: undefined }) readonly search;
  @Prop({ default: 'noWrapHeader' }) readonly tableClass;
  @Prop({ default: () => null }) readonly backendFilter;
  @Prop({ default: () => [] }) readonly value;

  selectedItems: any[] = [];
  outlined: Boolean = true;
  dialog: Boolean = false;

  @Watch('value')
  onChangeValue(value) {
    this.selectedItems = [...value];
  }

  @Emit('input')
  emitChange() {
    return this.selectedItems;
  }

  selectAll({ value }) {
    if (value) {
      this.selectedItems = [...this.items];
    } else {
      this.selectedItems = [];
    }
  }

  @Watch('filters', { deep: true })
  async onChangeFilters(filters) {
    // Clear filters
    _.forEach(_.entries(filters), ([key, value]: any) => {
      if (!value || value === '' || (_.isArray(value) && value.length === 0)) {
        delete filters[key];
      }
      const header = _.find(this.headers, { value: key });
      if (!!header && header.type === 'daterange' && !!value && value.length !== 2) {
        delete filters[key];
      }
    });

    if (!!this.backendFilter) {
      clearTimeout(this.backendFilterTimeout);
      if (this.isFiltering) {
        this.backendFilterTimeout = setTimeout(async () => {
          this.componentLoading = true;
          await this.backendFilter({
            filters: encodeURIComponent(JSON.stringify(filters)),
          });
          this.componentLoading = false;
        }, 700);
      } else {
        await this.backendFilter({ limit: this.items.length + 50 });
      }
    }
  }

  @Watch('items', { deep: true, immediate: true })
  onChangeItems() {
    this.loadSelectInputOptions();
  }

  lastLimit: number = 0;
  componentLoading = false;
  filters = {};
  selectInputOptions = {};
  backendFilterTimeout: any = null;

  get headersWithTypes() {
    return _.filter(this.headers, (h) => !_.isNil(h.type));
  }

  get booleanHeaders() {
    return _.filter(this.headers, { type: 'boolean' });
  }

  get isLoading() {
    return this.loading || this.componentLoading;
  }

  get dateTimeHeaders() {
    return _.filter(this.headers, { type: 'datetime' });
  }

  get dateHeaders() {
    return _.filter(this.headers, (d) => _.includes(['date', 'daterange'], d.type));
  }

  get isFiltering() {
    return _.some(_.values(this.filters), (v) => !!v);
  }

  get filteredItems() {
    let items = this.items;
    // Filtro no backEnd
    if (!!this.backendFilter) {
      return items;
    }

    // Filtro no frontEnd
    _.forEach(_.entries(this.filters), ([value, filter]: any) => {
      if (filter) {
        // Header correspondente
        const header = _.find(this.headers, { value: value });

        if (header && header.type) {
          // NUMBER
          if (header.type === 'number') {
            items = _.filter(items, (i) => (this.getNestedKey(i, value) || null) == filter);

            // VARCHAR
          } else if (header.type === 'varchar') {
            items = _.filter(items, (i) => {
              const nestedValue = this.getNestedKey(i, value) || '';
              return _.includes(_.lowerCase(nestedValue), _.lowerCase(filter));
            });

            // DATE & DATETIME
          } else if (_.includes(['date', 'datetime'], header.type)) {
            let parsedFilterDate = new Date();
            if (header.format === 'ISO' || !header.format) {
              parsedFilterDate = parseISO(filter);
              if (header.dateOrder === 'after') {
                items = _.filter(items, (i) => isAfter(parseISO(i[value]), parsedFilterDate));
              } else {
                items = _.filter(items, (i) => isBefore(parseISO(i[value]), parsedFilterDate));
              }
            } else {
              parsedFilterDate = parse(filter, header.format, new Date());
              if (header.dateOrder === 'after') {
                items = _.filter(items, (i) => isAfter(parse(i[value], header.format, new Date()), parsedFilterDate));
              } else {
                items = _.filter(items, (i) => isBefore(parse(i[value], header.format, new Date()), parsedFilterDate));
              }
            }
            // DATERANGE
          } else if (_.includes('daterange', header.type)) {
            // Verificar PQ items somem qdo este filtro é aplicado
            if (filter.length == 2) {
              let parsedFilterDateStart = parse(filter[0], header.format || 'yyyy-MM-dd', new Date());

              let parsedFilterDateEnd = parse(filter[1], header.format || 'yyyy-MM-dd', new Date());

              items = _.filter(
                items,
                (i) =>
                  isAfter(
                    parse(i[value].slice(0, 10), header.format || 'yyyy-MM-dd', new Date()),
                    parsedFilterDateStart
                  ) &&
                  isBefore(parse(i[value].slice(0, 10), header.format || 'yyyy-MM-dd', new Date()), parsedFilterDateEnd)
              );
            }

            // SELECT & AUTOCOMPLETE
          } else if (_.includes(['select', 'autocomplete', 'boolean'], header.type)) {
            items = _.filter(items, (i) => _.includes(String(filter), String(i[value])));
          }
        }
      }
    });
    return items;
  }

  async handleScrolledToBottom(isVisible) {
    if (!isVisible || this.isLoading) {
      return;
    }

    if (this.lastLimit >= this.items.length + 50) return;
    this.componentLoading = true;
    await this.backendFilter({
      limit: this.items.length + 50,
      filters: encodeURIComponent(JSON.stringify(this.filters)),
    });
    this.componentLoading = false;
  }

  getNestedKey(object: any, key: string) {
    const keys = key.split('.');
    _.forEach(keys, (key, index) => {
      object = _.get(object, key);
      if (!object) return;
    });
    return object;
  }

  loadSelectInputOptions() {
    if (this.items.length > 0) {
      // Monta Select/Autocomplete input Options
      const selectHeaders = _.filter(this.headers, (h) => _.includes(['select', 'autocomplete'], h.type));

      for (let header of selectHeaders) {
        this.selectInputOptions[header.value] = _.orderBy(_.keys(_.groupBy(this.items, header.value)));
      }
    }

    this.lastLimit = this.filteredItems.length;
  }
}
